import Axios from "axios";
import { clockSkewMs } from "../config";

/**
 * Reads a JWT's `exp` without the side effects of constructing a JWTToken
 * (which writes localStorage and the Axios default header).
 *
 * "Should we proactively treat this token as dead?" — tolerant, so a client
 * whose clock runs fast does not sign itself out of a session the server is
 * still perfectly happy with.
 */
export const isTokenExpired = (jwt: string | null): boolean => {
  const { expAt } = readTokenTiming(jwt);
  if (!expAt) return true;
  // Tolerance comes from config (clockSkewSeconds).
  return Date.now() > expAt + clockSkewMs();
};

/**
 * "The server just rejected this — was it expiry or displacement?" — the
 * opposite bias, and deliberately NOT the same predicate.
 *
 * The auth server answers 402/403 for both causes, so the client has to
 * choose an explanation. Asking the tolerant question here gets it backwards:
 * for the first 30 seconds after `exp` the tolerant answer is "not expired",
 * so every ordinary expiry would be reported as "you logged in somewhere
 * else". At or past `exp`, expiry is by far the better explanation for a
 * rejection, so classify with no tolerance at all.
 */
export const isTokenPastExpiry = (jwt: string | null): boolean => {
  const { expAt } = readTokenTiming(jwt);
  if (!expAt) return true;
  return Date.now() >= expAt;
};

/**
 * `exp` and `lifeTime` off the payload, in ms. `lifeTime` is the lifetime the
 * auth server actually granted (it clamps whatever was requested), which lets
 * the client refresh at a fraction of the real lifetime instead of guessing.
 * Zeroes mean unparseable.
 */
export const readTokenTiming = (
  jwt: string | null,
): { expAt: number; lifeTimeMs: number } => {
  if (!jwt) return { expAt: 0, lifeTimeMs: 0 };
  try {
    const segment = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(segment));
    return {
      expAt: (payload.exp || 0) * 1000,
      lifeTimeMs: (payload.lifeTime || 0) * 1000,
    };
  } catch {
    return { expAt: 0, lifeTimeMs: 0 };
  }
};

export class JWTToken {
  private _jwt: string = "";
  private _expiresAt: Date = new Date(0);
  private _sid: string = "";
  private _username: string = "";

  constructor(jwt: string | null) {
    if (jwt) {
      this.init(jwt);
    } else {
      // Clear the default header too. Without this a logout leaves the old
      // bearer token attached to every subsequent request from this tab.
      delete Axios.defaults.headers.common["Authorization"];
    }
  }

  private init(jwt: string) {
    this._jwt = jwt;
    try {
      // base64url -> base64 before decoding: JWT segments use -/_ , and atob
      // rejects them.
      const segment = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(segment));
      this._expiresAt = new Date((payload.exp || 0) * 1000);
      this._sid = payload.sid || "";
      this._username = payload.username || "";
    } catch {
      this._expiresAt = new Date(0);
    }
    Axios.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
    localStorage.setItem("token", jwt);
  }

  get accessToken() {
    return this._jwt;
  }

  get expiresAt() {
    return this._expiresAt;
  }

  /** Session id. Every token the auth server issues carries one. */
  get sid() {
    return this._sid;
  }

  get username() {
    return this._username;
  }

  get isExpired() {
    return Date.now() > this._expiresAt.getTime();
  }

  get isAuthenticated() {
    return !!this._jwt && !this.isExpired;
  }
}
